import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-all duration-300 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 font-body tracking-wide active:scale-[0.97] relative overflow-hidden group",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md rounded-md before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent before:-translate-x-full hover:before:translate-x-full before:transition-transform before:duration-700",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-md",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground hover:shadow-sm rounded-md hover:border-gold/50 before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-gold/5 before:to-transparent before:-translate-x-full hover:before:translate-x-full before:transition-transform before:duration-700",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 hover:shadow-sm rounded-md",
        ghost: "hover:bg-accent hover:text-accent-foreground rounded-md",
        link: "text-primary underline-offset-4 hover:underline",
        luxury: "bg-gradient-to-r from-gold to-bronze text-cream shadow-[0_4px_30px_hsl(38_55%_50%_/_0.2)] hover:shadow-[0_12px_50px_hsl(38_55%_50%_/_0.35)] hover:scale-[1.03] hover:-translate-y-0.5 uppercase tracking-[0.2em] before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/20 before:to-transparent before:-translate-x-full hover:before:translate-x-full before:transition-transform before:duration-700",
        "luxury-outline": "border-2 border-gold bg-transparent text-gold hover:bg-gold hover:text-cream hover:shadow-[0_8px_40px_hsl(38_55%_50%_/_0.25)] hover:scale-[1.02] uppercase tracking-[0.2em]",
        hero: "bg-cream/10 text-cream border border-cream/30 backdrop-blur-sm hover:bg-cream/20 hover:border-cream/50 hover:shadow-[0_8px_30px_hsl(40_33%_98%_/_0.15)] uppercase tracking-[0.15em] before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent before:-translate-x-full hover:before:translate-x-full before:transition-transform before:duration-700",
        "hero-solid": "bg-cream text-charcoal hover:bg-cream/90 hover:shadow-[0_12px_50px_hsl(30_10%_15%_/_0.15)] hover:scale-[1.02] uppercase tracking-[0.15em] before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-charcoal/10 before:to-transparent before:-translate-x-full hover:before:translate-x-full before:transition-transform before:duration-700",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-14 rounded-md px-10 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
